<script setup lang="ts">
definePageMeta({
  key: (route) => route.fullPath,
});

const route = useRoute();

const { data: product, error } = await useFetch(
  `/api/products/${route.params.handle}`,
);

if (error.value) {
  throw createError({ statusCode: 404, statusMessage: "Product not found" });
}

const arrowRight = "/images/icons/arrow-right-handle.svg";

const activeTab = ref<"material" | "care" | "shipping">("material");
const selectedImage = ref(0);
const selectedVariant = ref(product.value?.variants[0] ?? null);

watch(selectedVariant, (variant) => {
  if (!variant?.image || !product.value?.images) return
  const idx = product.value.images.indexOf(variant.image)
  if (idx !== -1) selectedImage.value = idx
})

const { data: recommendations } = await useFetch(
  `/api/products/${route.params.handle}/recommendations`,
);

const { data: storyBanners } = await useFetch(
  `/api/products/${route.params.handle}/stories`,
);

// Story banner carousel
const activeStory = ref(0);
let storyTimer: ReturnType<typeof setInterval> | null = null;

function goToStory(index: number) {
  activeStory.value = index;
  resetStoryTimer();
}

function resetStoryTimer() {
  if (storyTimer) clearInterval(storyTimer);
  if (storyBanners.value && storyBanners.value.length > 1) {
    storyTimer = setInterval(() => {
      activeStory.value = (activeStory.value + 1) % storyBanners.value!.length;
    }, 5000);
  }
}

onMounted(() => resetStoryTimer());
onUnmounted(() => { if (storyTimer) clearInterval(storyTimer); });

const isComingSoon = computed(() => product.value?.tags?.includes('coming-soon') ?? false)

const scrollToSubscribe = () => {
  document.getElementById('footer-subscribe')?.scrollIntoView({ behavior: 'smooth' })
}

const { addToCart: addToCartComposable } = useCart();
const isAddingToCart = ref(false);
const addedToCart = ref(false);

async function addToCart() {
  if (!selectedVariant.value) return;
  isAddingToCart.value = true;
  try {
    await addToCartComposable(selectedVariant.value.id, 1);
    addedToCart.value = true;
    setTimeout(() => {
      addedToCart.value = false;
    }, 2000);
  } finally {
    isAddingToCart.value = false;
  }
}
</script>

<template>
  <div>
    <!-- BREADCRUMB -->
    <div class="bg-white flex items-center gap-3 md:gap-4 h-12 px-4 md:px-20 w-full overflow-hidden mt-[72px]">
      <NuxtLink
        to="/shop"
        class="font-label font-light text-xs leading-4 tracking-wider text-muted hover:text-brand transition-colors whitespace-nowrap"
        >Goods</NuxtLink
      >
      <span class="font-label font-light text-xs text-muted">/</span>
      <span class="font-label font-light text-xs leading-4 tracking-wider text-muted whitespace-nowrap"
        >{{ product!.category }}</span
      >
      <span class="font-label font-light text-xs text-muted">/</span>
      <span class="font-label font-light text-xs leading-4 tracking-wider text-dark whitespace-nowrap"
        >{{ product!.name }}</span
      >
    </div>

    <!-- PRODUCT DETAIL -->
    <section class="flex flex-col items-center py-8 md:py-24 w-full">
      <div class="flex flex-col md:flex-row gap-8 items-start w-full max-w-7xl px-4 md:px-20">
        <!-- IMAGE GALLERY -->
        <div class="flex flex-col gap-4 md:gap-6 items-start w-full md:flex-1 md:min-w-0">
          <!-- Main image -->
          <div class="relative h-80 md:h-[580px] w-full overflow-hidden">
            <span v-if="isComingSoon" class="absolute top-3 left-3 z-10 font-label font-light text-sm uppercase tracking-widest bg-dark text-brand-light px-3 py-1">
              Coming Soon
            </span>
            <img
              :src="product!.images[selectedImage] || '/images/products/ranch-jacket-main.png'"
              :alt="product!.name"
              class="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <!-- Thumbnails -->
          <div v-if="product!.images.length > 1" class="flex gap-2.5 md:gap-4 items-start w-full overflow-x-auto">
            <button
              v-for="(img, i) in product!.images"
              :key="i"
              class="relative shrink-0 size-[72px] md:size-[130px] overflow-hidden transition-opacity"
              :class="selectedImage === i ? 'opacity-100 ring-2 ring-brand' : 'opacity-60 hover:opacity-100'"
              @click="selectedImage = i"
            >
              <img :src="img" alt="" class="absolute inset-0 w-full h-full object-cover" />
            </button>
          </div>
        </div>

        <!-- PRODUCT INFO -->
        <div class="flex flex-col gap-6 md:gap-8 items-start w-full md:flex-1 md:min-w-0 border-b border-disabled pb-2">
          <!-- Title & price -->
          <div class="flex flex-col gap-2 items-start">
            <p class="font-label font-light text-xs leading-4 tracking-wider text-brand">
              {{ product!.category }}
            </p>
            <h1 class="font-serif font-normal text-[32px] md:text-5xl leading-10 md:leading-tight text-dark">
              {{ product!.name }}
            </h1>
            <p v-if="isComingSoon" class="px-3 py-1 bg-dark text-brand-light font-label font-light text-sm leading-4 tracking-wider uppercase">Coming Soon</p>
            <p class="font-serif font-normal text-xl md:text-2xl leading-6 text-dark">
              {{ selectedVariant?.price ?? product!.price }}
            </p>
          </div>

          <div class="bg-stroke h-px w-full" />

          <!-- Description -->
          <div class="flex flex-col gap-2 items-start w-full">
            <div
              class="font-sans font-light text-[13px] md:text-sm leading-5 text-muted"
              v-html="product!.descriptionHtml"
            />
          </div>

          <template v-if="!isComingSoon">
            <!-- Variant selector -->
            <div v-if="product!.variants.length > 1" class="flex flex-col gap-1 items-start w-full md:w-60">
              <label class="font-label font-light text-[13px] md:text-sm leading-5 text-secondary whitespace-nowrap">
                SELECT OPTION
              </label>
              <select
                class="bg-white border border-stroke h-11 px-3 w-full rounded font-sans font-light text-[13px] md:text-sm leading-5 text-muted appearance-none cursor-pointer focus:outline-none focus:border-brand"
                @change="(e) => {
                  const v = product!.variants.find((v) => v.id === (e.target as HTMLSelectElement).value);
                  if (v) selectedVariant = v;
                }"
              >
                <option
                  v-for="variant in product!.variants"
                  :key="variant.id"
                  :value="variant.id"
                  :disabled="!variant.availableForSale"
                >
                  {{ variant.title }}{{ !variant.availableForSale ? " — Sold Out" : "" }}
                </option>
              </select>
            </div>

            <!-- Add to cart -->
            <button
              class="bg-primary flex gap-1 h-[52px] items-center justify-center p-2 w-full hover:bg-primary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isAddingToCart || !selectedVariant?.availableForSale"
              @click="addToCart"
            >
              <span class="font-label font-light text-sm md:text-lg  text-white">
                {{ isAddingToCart ? "ADDING..." : addedToCart ? "ADDED!" : `ADD TO CART — ${selectedVariant?.price ?? product!.price}` }}
              </span>
            </button>
          </template>
          <div v-else class="flex flex-col items-center gap-2 w-full">
            <button
              class="bg-dark flex gap-1 h-[52px] items-center justify-center p-2 w-full hover:bg-dark/80 transition-colors"
              @click="scrollToSubscribe"
            >
              <span class="font-label font-light text-sm md:text-lg text-brand-light">JOIN THE WAITLIST</span>
            </button>
            <p class="font-sans font-light text-xs leading-4 text-muted">
              Be the first to know when this drops
            </p>
          </div>

          <!-- Tabs -->
          <div class="flex flex-col gap-2 items-start w-full">
            <div class="border-b border-disabled flex gap-2 md:gap-4 items-start w-full overflow-x-auto">
              <button
                class="flex items-center justify-center p-2 font-label font-light text-[13px] md:text-sm leading-5 whitespace-nowrap transition-colors"
                :class="activeTab === 'material' ? 'text-dark' : 'text-brand'"
                @click="activeTab = 'material'"
              >Material &amp; Details</button>
              <button
                class="flex items-center justify-center p-2 font-label font-light text-[13px] md:text-sm leading-5 whitespace-nowrap transition-colors"
                :class="activeTab === 'care' ? 'text-dark' : 'text-brand'"
                @click="activeTab = 'care'"
              >Care &amp; Provenance</button>
              <button
                class="flex items-center justify-center p-2 font-label font-light text-[13px] md:text-sm leading-5 whitespace-nowrap transition-colors"
                :class="activeTab === 'shipping' ? 'text-dark' : 'text-brand'"
                @click="activeTab = 'shipping'"
              >Shipping &amp; Returns</button>
            </div>

            <div class="flex items-start justify-between w-full pt-2">
              <div v-if="activeTab === 'material'">
                <ul class="font-sans font-light text-[13px] md:text-sm leading-5 text-muted list-disc pl-5 space-y-0">
                  <li>Premium quality materials</li>
                  <li>Crafted with care</li>
                </ul>
              </div>
              <div v-else-if="activeTab === 'care'">
                <ul class="font-sans font-light text-[13px] md:text-sm leading-5 text-muted list-disc pl-5 space-y-0">
                  <li>Follow care label instructions</li>
                  <li>Made with intention</li>
                </ul>
              </div>
              <div v-else-if="activeTab === 'shipping'">
                <ul class="font-sans font-light text-[13px] md:text-sm leading-5 text-muted list-disc pl-5 space-y-0">
                  <li>Free shipping on orders over $150</li>
                  <li>Ships within 3–5 business days</li>
                  <li>Returns accepted within 30 days, unworn</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- PRODUCT STORY BANNERS (carousel) -->
    <section
      v-if="storyBanners?.length"
      class="relative flex items-stretch min-h-80 md:h-[480px] w-full overflow-hidden"
    >
      <!-- Slides -->
      <template v-for="(story, index) in storyBanners" :key="index">
        <Transition name="story-fade">
          <div v-show="activeStory === index" class="absolute inset-0">
            <img
              v-if="story.image?.url"
              :src="story.image.url"
              :alt="story.image.altText ?? ''"
              class="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
            <div class="absolute inset-0 bg-black/40 pointer-events-none" />
            <div class="absolute inset-0 flex items-stretch">
              <div class="hidden md:block md:flex-1" />
              <div class="relative bg-dark/50 flex flex-col w-full md:w-[622px] md:shrink-0 items-center justify-center px-6 md:px-10 py-12 md:py-0">
                <div class="flex gap-6 items-center w-full">
                  <div class="bg-primary self-stretch shrink-0 w-[3px]" />
                  <div class="flex flex-col gap-6 items-start flex-1 min-w-0">
                    <div class="flex flex-col gap-2 items-start text-white w-full">
                      <p class="font-label font-light text-[13px] md:text-base leading-6">As seen in the journal</p>
                      <div class="flex flex-col gap-2 items-start">
                        <p class="font-serif font-normal text-2xl md:text-4xl leading-8 md:leading-[44px]">
                          {{ story.title }}
                        </p>
                        <p
                          v-if="story.description"
                          class="font-sans font-light text-[13px] md:text-sm leading-5 text-white/90"
                        >
                          {{ story.description }}
                        </p>
                      </div>
                    </div>
                    <NuxtLink :to="story.blogUrl">
                      <AppButton bgColor="bg-brand" type="Primary" size="Medium" label="Read the full story" :showIconRight="true" />
                    </NuxtLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </template>

      <!-- Dots -->
      <div v-if="storyBanners.length > 1" class="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        <button
          v-for="(_, i) in storyBanners"
          :key="i"
          class="size-2.5 rounded-full transition-colors"
          :class="activeStory === i ? 'bg-white' : 'bg-white/40 hover:bg-white/70'"
          @click="goToStory(i)"
        />
      </div>
    </section>

    <!-- YOU MAY ALSO LIKE -->
    <section v-if="recommendations && recommendations.length" class="py-16 px-4 md:px-20 w-full max-w-7xl mx-auto">
      <div class="flex flex-col gap-8">
        <h2 class="font-serif font-normal text-2xl md:text-3xl text-dark">You may also like</h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <AppCard
            v-for="item in recommendations"
            :key="item.id"
            type="Product-full"
            :title="item.name"
            :price="item.price"
            :image="item.image"
            :categories="item.category"
            :slug="`/shop/${item.handle}`"
            :tags="item.tags"
          />
        </div>
      </div>
    </section>

  </div>
</template>

<style scoped>
.story-fade-enter-active,
.story-fade-leave-active {
  transition: opacity 0.6s ease;
}
.story-fade-enter-from,
.story-fade-leave-to {
  opacity: 0;
}
</style>
